import { useState } from "react"
import { SparklesIcon } from "@heroicons/react/24/outline"
import { api } from "aldoo-ra/API"

const aiImageAPI = api("aldoo-pulse/ai-image")

function toWebP(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      canvas.getContext("2d").drawImage(img, 0, 0)
      resolve(canvas.toDataURL("image/webp", 0.88))
    }
    img.src = dataUrl
  })
}

/**
 * AI Image Generation dialog (no credits required).
 * Props:
 *   onUse(dataUrl)  — called when user clicks Use
 *   onClose()
 */
export default function AIImageDialog({ onUse, onClose }) {
  const [prompt, setPrompt] = useState("")
  const [imageDataUrl, setImageDataUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleGenerate = async () => {
    const trimmed = prompt.trim()
    if (!trimmed) { setError("Please enter a prompt."); return }
    setLoading(true)
    setError("")
    setImageDataUrl(null)
    try {
      const fullPrompt = trimmed.toLowerCase().includes("no text") ? trimmed : `${trimmed}, no text`
      const res = await aiImageAPI({ aiImage: { prompt: fullPrompt, aspectRatio: "1:1" } })
      if (res?.error) { setError("Image generation failed. Please try again."); return }
      const mime = res.mimeType || "image/jpeg"
      const webpDataUrl = await toWebP(`data:${mime};base64,${res.image}`)
      setImageDataUrl(webpDataUrl)
    } catch {
      setError("Image generation failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleUse = () => {
    if (!imageDataUrl) return
    onUse(imageDataUrl)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-admin_paper rounded-2xl shadow-xl w-full md:w-[85%] h-full md:h-auto md:min-h-[85vh] md:max-h-[calc(100vh-2rem)] mx-0 md:mx-4 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-6 py-4 border-b border-admin_bordered shrink-0">
          <SparklesIcon className="w-5 h-5 text-admin_primary" />
          <span className="text-admin_text font-semibold text-base">AI Generate Image</span>
          <button
            onClick={onClose}
            className="ml-auto p-1.5 rounded-full hover:bg-admin_neutral_1 transition text-admin_text_secondary"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — image preview takes all remaining space */}
        <div className="flex-1 flex flex-col min-h-0 p-4 gap-3">
          {/* Preview — fills all available space */}
          <div className="flex-1 min-h-0 rounded-xl border border-admin_bordered bg-admin_neutral flex items-center justify-center overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-admin_primary/20 border-t-admin_primary rounded-full animate-spin" />
                <span className="text-sm text-admin_text_secondary">Generating…</span>
              </div>
            ) : imageDataUrl ? (
              <img src={imageDataUrl} alt="Generated" className="w-full h-full object-contain" />
            ) : (
              <span className="text-sm text-admin_text_secondary text-center px-8">
                Your generated image will appear here
              </span>
            )}
          </div>

          {/* Prompt row */}
          <div className="flex gap-2 shrink-0 items-start">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleGenerate() }}
              placeholder="Describe the image you want to generate…"
              rows={2}
              className="flex-1 rounded-lg border border-admin_bordered bg-admin_canvas text-admin_text text-sm px-3 py-2 resize-none outline-none focus:border-admin_primary transition"
            />
            <div className="flex flex-col gap-2 shrink-0">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-admin_bordered text-admin_text text-sm font-medium hover:bg-admin_canvas transition disabled:opacity-50 whitespace-nowrap"
              >
                <SparklesIcon className="w-4 h-4 text-admin_primary" />
                {loading ? "Generating…" : "Generate"}
              </button>
              {imageDataUrl && (
                <button
                  onClick={handleUse}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-admin_primary text-white text-sm font-medium hover:bg-admin_primary_dk transition disabled:opacity-50 whitespace-nowrap"
                >
                  Use this image
                </button>
              )}
            </div>
          </div>

          {error && <p className="text-xs text-admin_error shrink-0">{error}</p>}
        </div>
      </div>
    </div>
  )
}
